import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { authService } from "../../services/authService";
import "../../assets/styles/AuthForm.css";
import "../../assets/styles/bootstrap.min.css";
import i1 from "../../assets/image/i1.png";
import logo from "../../assets/image/logo.png";
import { ROUTES } from "../../utils/constants";
import { validateForgotPasswordForm } from "../../utils/validators";
import { ValidationErrors } from "../../types/auth.types";
import axios from "axios";
import Footer from '../../components/Layout/Footer';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors = validateForgotPasswordForm(email);
    setValidationErrors(errors);

    if (Object.keys(errors).length !== 0) return;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await authService.forgotPassword({ email });

      if (response?.data?.status === "failed") {
        throw new Error(response.data.message || "Failed to send reset email");
      }

      setSuccessMessage("A password reset link has been sent your registered email.");
      setEmail("");

    } catch (err) {
      setError("Unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const renderFieldError = (field: string) =>
    touched[field] && validationErrors[field] ? (
      <span className="error-message">{validationErrors[field]}</span>
    ) : null;

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  return (
    <div className="auth-page" style={{ overflowY: "auto", height: "100vh" }}>
      <div className="auth-form-container">
        <div className="auth-decorative-panel">
          <div className="decorative-content">
            <img src={i1} alt="Corecrate Image" />
          </div>
        </div>

        <div className="auth-form-wrapper mr-5 ml-5">
          <img
            src={logo}
            alt="Corecrate Logo"
            className="mb-5"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/")}
          />

          <p className="text-muted mb-4">
            Enter your email below and we’ll send you a password reset email.
          </p>

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email address</label>
              <input
                id="email"
                type="email"
                name="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                onBlur={() => handleBlur("email")}
              />
              {renderFieldError("email")}
            </div>

            {error && (
              <div className="error-message" style={{ marginBottom: "15px" }}>
                {error}
              </div>
            )}

            {successMessage && (
              <div
                style={{
                  backgroundColor: "#d4edda",
                  color: "#155724",
                  border: "1px solid #c3e6cb",
                  padding: "10px",
                  borderRadius: "5px",
                  marginBottom: "15px",
                }}
              >
                ✅ {successMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email.trim()}
              className="submit-button"
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Sending...
                </>
              ) : (
                "Reset Password"
              )}
            </button>

            <div className="form-footer">
              Nevermind,
              <Link to={ROUTES.SIGNIN}> I remembered it.</Link>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ForgotPasswordPage;